'use client';

import { FormField } from '@/features/forms/FormPrimitives';
import readHubMessage from '@/features/hub/shared/readHubMessage';
import { clientFetch } from '@/lib/api/clientFetch';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { canCurrentUserManageHubTeam } from '../teamRoles';

function formatRequestTime(value, locale, fallbackLabel = '') {
  if (!value) {
    return fallbackLabel;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return fallbackLabel;
  }

  try {
    return new Intl.DateTimeFormat(locale || 'en', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

export default function HubTeamModerationPanel({
  team,
  currentUser,
  locale,
  messages = {},
}) {
  const router = useRouter();
  const canModerate = Boolean(
    currentUser && canCurrentUserManageHubTeam(team, currentUser),
  );

  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(canModerate);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [activeRequestKey, setActiveRequestKey] = useState('');
  const msg = (path, fallback = '', replacements = {}) =>
    readHubMessage(messages, path, fallback, replacements);

  useEffect(() => {
    let isMounted = true;

    const loadRequests = async () => {
      if (!canModerate) {
        setRequests([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setErrorMessage('');

      try {
        const response = await clientFetch(
          `/api/hub/teams/${team.teamNumber}/join-requests`,
        );

        if (!isMounted) {
          return;
        }

        setRequests(Array.isArray(response?.requests) ? response.requests : []);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setErrorMessage(
          error?.data?.message ||
            error?.message ||
            msg(
              'teams.moderation.errors.loadRequests',
              'Unable to load team join requests.',
            ),
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadRequests();

    return () => {
      isMounted = false;
    };
  }, [canModerate, team.teamNumber]);

  if (!canModerate) {
    return null;
  }

  const updateRequestStatus = async (userNumber, status) => {
    const requestKey = `${userNumber}:${status}`;
    const targetRequest = requests.find(
      (request) => request.userNumber === userNumber,
    );
    const requestLabel =
      targetRequest?.displayName || targetRequest?.name || userNumber;

    setErrorMessage('');
    setStatusMessage('');
    setActiveRequestKey(requestKey);

    try {
      await clientFetch(
        `/api/hub/teams/${team.teamNumber}/join-requests/${encodeURIComponent(userNumber)}`,
        {
          method: 'PATCH',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({ status }),
        },
      );

      setRequests((currentRequests) =>
        currentRequests.filter((request) => request.userNumber !== userNumber),
      );
      setStatusMessage(
        msg(
          status === 'approved'
            ? 'teams.moderation.approvedToast'
            : 'teams.moderation.rejectedToast',
          status === 'approved'
            ? 'Approved team request from {{name}}.'
            : 'Declined team request from {{name}}.',
          { name: requestLabel },
        ),
      );
      router.refresh();
    } catch (error) {
      setErrorMessage(
        error?.data?.message ||
          error?.message ||
          msg(
            'teams.moderation.errors.updateStatus',
            'Unable to update the team request status.',
          ),
      );
    } finally {
      setActiveRequestKey('');
    }
  };

  return (
    <section className="app-panel hub-action-panel hub-action-panel--teams">
      <div className="hub-action-panel__header">
        <div>
          <p className="hub-section__eyebrow hub-section__eyebrow--teams">
            {msg('teams.moderation.eyebrow', 'Owner review')}
          </p>
          <h2>{msg('teams.moderation.title', 'Membership requests')}</h2>
        </div>
        <span className="hub-pill hub-pill--games">
          {msg('teams.moderation.pendingCount', '{{count}} pending', {
            count: requests.length,
          })}
        </span>
      </div>

      <p className="shop-panel__meta">
        {msg(
          'teams.moderation.hint',
          'Review incoming requests to join this team without leaving the page.',
        )}
      </p>

      {statusMessage ? (
        <div className="auth-message auth-message--success">
          {statusMessage}
        </div>
      ) : null}

      {errorMessage ? (
        <div className="auth-message auth-message--error">{errorMessage}</div>
      ) : null}

      {isLoading ? (
        <p className="shop-panel__meta">
          {msg('teams.moderation.loading', 'Loading membership requests...')}
        </p>
      ) : requests.length > 0 ? (
        <div className="hub-request-list">
          {requests.map((request) => {
            const requestLabel =
              request.displayName || request.name || request.userNumber;

            return (
              <article
                key={request.userNumber}
                className="hub-request-card surface-card"
              >
                <div className="hub-request-card__header">
                  <div className="hub-request-card__identity">
                    <h3>{requestLabel}</h3>
                    <p className="product-card__meta">
                      {request.userNumber} •{' '}
                      {msg('teams.moderation.requestedLabel', 'Requested')}{' '}
                      {formatRequestTime(
                        request.createdAt,
                        locale,
                        msg('teams.moderation.unknownTime', 'Unknown time'),
                      )}
                    </p>
                  </div>
                  <span className="hub-pill hub-pill--warning">
                    {msg('teams.joinPanel.pendingBadge', 'Pending')}
                  </span>
                </div>

                {request.message ? (
                  <FormField
                    as="textarea"
                    label={msg(
                      'teams.moderation.requestMessageLabel',
                      'Their message',
                    )}
                    rows={3}
                    value={request.message}
                    readOnly
                    hideLabel
                    inputClassName="ps-3"
                    fieldClassName="auth-field"
                    controlWrapClassName="mt-2"
                  />
                ) : (
                  <p className="shop-panel__meta">
                    {msg(
                      'teams.moderation.noMessage',
                      'No message was included with this request.',
                    )}
                  </p>
                )}

                <div className="auth-form__actions">
                  <button
                    type="button"
                    className="app-button app-button--primary app-button--compact"
                    onClick={() =>
                      updateRequestStatus(request.userNumber, 'approved')
                    }
                    disabled={Boolean(activeRequestKey)}
                  >
                    {activeRequestKey === `${request.userNumber}:approved`
                      ? msg('teams.moderation.approveLoading', 'Approving...')
                      : msg('teams.moderation.approve', 'Approve')}
                  </button>
                  <button
                    type="button"
                    className="app-button app-button--ghost app-button--compact"
                    onClick={() =>
                      updateRequestStatus(request.userNumber, 'rejected')
                    }
                    disabled={Boolean(activeRequestKey)}
                  >
                    {activeRequestKey === `${request.userNumber}:rejected`
                      ? msg('teams.moderation.rejectLoading', 'Declining...')
                      : msg('teams.moderation.reject', 'Decline')}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <p className="shop-panel__meta text-muted mt-4">
          {msg('teams.moderation.empty', 'No pending team requests.')}
        </p>
      )}
    </section>
  );
}
